/**
 * BackOrderHistoryPage - /back-orders (single store).
 *
 * Read-only status grid of the current store's back orders, mirroring OrderHistoryPage.
 * Editing a back order still happens on the product pages / the back-order offcanvas.
 */
import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import authService from "../../services/authService";
import { BACKORDER_STATUS_TABS, BackOrderRow } from "../../components/backorder/BackOrdersGrid";

const BackOrderHistoryPage = () => {
  const [activeTab, setActiveTab] = useState("open");
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ column: null, direction: "desc" });
  const pageSize = 10;

  // Debounce search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== debouncedSearchTerm) {
        setDebouncedSearchTerm(searchTerm);
        setPage(1);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearchTerm]);

  const currentStore = authService.getCurrentStore();
  const clientId = currentStore?.id; // store.id is the clientId

  const buildQuery = useCallback(
    (statusFilter) => {
      let filter = `ClientID eq ${clientId}`;
      if (statusFilter) filter += ` and ${statusFilter}`;

      if (debouncedSearchTerm.trim()) {
        const term = debouncedSearchTerm.trim();
        filter += ` and contains(cast(ID, 'Edm.String'), '${term}')`;
      }

      let orderBy = "DateCreated desc";
      if (sortConfig.column === "datePlaced") {
        orderBy = `DateCreated ${sortConfig.direction}`;
      }

      return {
        $filter: filter,
        $orderby: orderBy,
        $expand: "ProductSkuVariant($select=ID,Name,LegacySKU,Flavor)",
        $top: pageSize,
        $skip: (page - 1) * pageSize,
        $count: true,
      };
    },
    [clientId, page, pageSize, sortConfig, debouncedSearchTerm],
  );

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["storeBackOrders", clientId, activeTab, page, sortConfig, debouncedSearchTerm],
    queryFn: async () => {
      if (!clientId) return { backOrders: [], totalCount: 0 };
      const statusConfig = BACKORDER_STATUS_TABS[activeTab];
      const response = await apiService.get("/storefront-odata/StorefrontBackOrders", buildQuery(statusConfig.filter));
      return {
        backOrders: response.value || [],
        totalCount: response["@odata.count"] || 0,
      };
    },
    enabled: !!clientId,
    staleTime: 2 * 60 * 1000,
  });

  const backOrders = data?.backOrders || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);

  const handleSort = (column) => {
    setSortConfig((prev) => ({
      column,
      direction: prev.column === column && prev.direction === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSortConfig({ column: null, direction: "desc" });
  };

  if (!clientId) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="mb-4">
              <i className="bi bi-shop display-1 text-muted"></i>
            </div>
            <h2 className="mb-3">Select a Store First</h2>
            <p className="text-muted mb-4">Please select a store to view your back orders.</p>
            <Link to="/account" className="btn btn-primary btn-lg">
              Select Store
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="mb-1">Back Orders</h1>
              <p className="text-muted mb-0">
                Products requested beyond stock for {currentStore?.name || "your store"}
              </p>
            </div>
            <Link to="/categories" className="btn btn-primary">
              <i className="bi bi-plus-lg me-2"></i>
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="row mb-4">
        <div className="col">
          <ul className="nav nav-tabs">
            {Object.entries(BACKORDER_STATUS_TABS).map(([key, config]) => (
              <li className="nav-item" key={key}>
                <button
                  className={`nav-link ${activeTab === key ? "active" : ""}`}
                  onClick={() => handleTabChange(key)}
                >
                  {config.label}
                  {activeTab === key && totalCount > 0 && (
                    <span className="badge bg-primary ms-2">{totalCount}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="alert alert-danger mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Failed to load back orders. Please try again.
          <button className="btn btn-link p-0 ms-2" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}

      {/* Search Bar */}
      {(totalCount > 0 || searchTerm || debouncedSearchTerm) && (
        <div className="row mb-3">
          <div className="col-md-4">
            <div className="input-group">
              <span className="input-group-text bg-white">
                <i className="bi bi-search text-muted"></i>
              </span>
              <input
                type="text"
                className="form-control"
                placeholder="Search by back order #..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => {
                    setSearchTerm("");
                    setDebouncedSearchTerm("");
                    setPage(1);
                  }}
                >
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading back orders...</p>
        </div>
      ) : backOrders.length === 0 && !debouncedSearchTerm && !searchTerm ? (
        <div className="text-center py-5">
          <i className={`bi ${BACKORDER_STATUS_TABS[activeTab].emptyIcon} display-1 text-muted mb-3 d-block`}></i>
          <h4 className="text-muted mb-3">{BACKORDER_STATUS_TABS[activeTab].emptyMessage}</h4>
          <p className="text-muted mb-4">
            When a product is out of stock, a <strong>Create Back Order</strong> option appears on the
            product so you can request it.
          </p>
          <Link to="/categories" className="btn btn-primary">
            Browse Products
          </Link>
        </div>
      ) : backOrders.length === 0 && debouncedSearchTerm ? (
        <div className="text-center py-5">
          <i className="bi bi-search display-1 text-muted mb-3 d-block"></i>
          <h4 className="text-muted mb-3">No back orders match &quot;{debouncedSearchTerm}&quot;</h4>
          <p className="text-muted mb-4">Try a different back order number or clear the search.</p>
        </div>
      ) : (
        <>
          <div className="card border-0 shadow-sm">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Back Order #</th>
                    <th
                      style={{ cursor: "pointer", userSelect: "none" }}
                      onClick={() => handleSort("datePlaced")}
                    >
                      Date Requested
                      <i
                        className={`bi ms-1 ${
                          sortConfig.column === "datePlaced"
                            ? sortConfig.direction === "asc"
                              ? "bi-sort-up"
                              : "bi-sort-down"
                            : "bi-arrow-down-up text-muted"
                        }`}
                      ></i>
                    </th>
                    <th>Product</th>
                    <th className="text-center">Qty</th>
                    <th className="text-end">Est. Total</th>
                    <th className="text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {backOrders.map((bo) => (
                    <BackOrderRow key={bo.id} backOrder={bo} showStore={false} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <p className="text-muted mb-0">
                Showing {(page - 1) * pageSize + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} back orders
              </p>
              <nav aria-label="Back orders pagination">
                <ul className="pagination mb-0">
                  <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <i className="bi bi-chevron-left"></i>
                    </button>
                  </li>
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (page <= 3) {
                      pageNum = idx + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = page - 2 + idx;
                    }
                    return (
                      <li key={pageNum} className={`page-item ${page === pageNum ? "active" : ""}`}>
                        <button className="page-link" onClick={() => setPage(pageNum)}>
                          {pageNum}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${page === totalPages ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BackOrderHistoryPage;
